import type { TemplateItem, CaseItem } from '../interface'

// 模板行选择配置
export const createTemplateRowSelection = (
  selectedTemplateIds: string[],
  templateIds: string[],
  templateData: any,
  selectedTemplateData: any[],
  setSelectedTemplateIds: (ids: string[]) => void,
  setSelectedTemplateData: (data: any[]) => void,
) => ({
  selectedRowKeys: selectedTemplateIds,
  onChange: (keys: React.Key[], selectedRows: TemplateItem[]) => {
    const newSelectedTemplateIds = keys.map(key => key.toString())

    // 检查是否是"全选所有"的情况
    const isSelectAllAllMode =
      newSelectedTemplateIds.length === templateIds.length && templateIds.length > 0

    // 如果是"全选所有"的情况，直接使用所有模板数据
    if (isSelectAllAllMode) {
      setSelectedTemplateIds(newSelectedTemplateIds)
      return
    }

    setSelectedTemplateIds(newSelectedTemplateIds)

    // 更新选中的模板数据
    const newSelectedTemplateData = templateData.list
      .filter(item => newSelectedTemplateIds.includes(item.templateId))
      .map(item => ({
        ...item,
        executeUpdate: item.executeUpdate,
      }))

    // 合并之前选中的和当前页选中的模板数据
    const updatedTemplateData = selectedTemplateData
      .filter(item => !templateData.list.some(t => t.templateId === item.templateId))
      .concat(newSelectedTemplateData)
    setSelectedTemplateData(updatedTemplateData)
  },
  onSelectAll: (selected: boolean) => {
    // 检查是否是"全选所有"的情况
    const isSelectAllAllMode =
      selectedTemplateIds.length === templateIds.length && templateIds.length > 0

    if (selected) {
      // 获取当前页的所有模板ID
      const currentPageIds = templateData.list.map(item => item.templateId)

      // 如果是"全选所有"的情况，直接使用所有模板ID，不进行合并操作
      if (isSelectAllAllMode) {
        // 不执行任何操作，因为handleSelectAll已经处理了
        return
      }

      // 合并当前页选中的ID和之前已选中的ID
      const newSelectedIds = [...new Set([...selectedTemplateIds, ...currentPageIds])]
      setSelectedTemplateIds(newSelectedIds)

      // 更新选中的模板数据
      const newSelectedTemplateData = templateData.list.map(item => ({
        ...item,
        executeUpdate: item.executeUpdate,
      }))

      // 合并之前选中的和当前页选中的模板数据
      const updatedTemplateData = selectedTemplateData
        .filter(item => !templateData.list.some(t => t.templateId === item.templateId))
        .concat(newSelectedTemplateData)
      setSelectedTemplateData(updatedTemplateData)
    } else {
      // 获取当前页的所有模板ID
      const currentPageIds = templateData.list.map(item => item.templateId)
      // 从已选中的ID中移除当前页的ID
      const newSelectedIds = selectedTemplateIds.filter(id => !currentPageIds.includes(id))
      setSelectedTemplateIds(newSelectedIds)

      // 更新选中的模板数据，移除当前页的模板
      const updatedTemplateData = selectedTemplateData.filter(
        item => !currentPageIds.includes(item.templateId),
      )
      setSelectedTemplateData(updatedTemplateData)
    }
  },
  onSelect: (record: TemplateItem, selected: boolean) => {
    if (selected) {
      // 添加选中的模板ID到已选中列表
      const newSelectedIds = [...new Set([...selectedTemplateIds, record.templateId])]
      setSelectedTemplateIds(newSelectedIds)

      // 更新选中的模板数据
      const newTemplate = {
        ...record,
        executeUpdate: record.executeUpdate,
      }
      const updatedTemplateData = selectedTemplateData
        .filter(item => item.templateId !== record.templateId)
        .concat(newTemplate)
      setSelectedTemplateData(updatedTemplateData)
    } else {
      // 从已选中列表中移除取消选中的模板ID
      const newSelectedIds = selectedTemplateIds.filter(id => id !== record.templateId)
      setSelectedTemplateIds(newSelectedIds)

      // 更新选中的模板数据
      const updatedTemplateData = selectedTemplateData.filter(
        item => item.templateId !== record.templateId,
      )
      setSelectedTemplateData(updatedTemplateData)
    }
  },
  getCheckboxProps: (record: TemplateItem) => ({
    disabled: record.type === 'system',
  }),
})

// 用例行选择配置
export const createCaseRowSelection = (
  selectedCaseIds: string[],
  caseData: any,
  setSelectedCaseIds: (ids: string[]) => void,
  updateTemplateSelectedCountMap: (ids: string[]) => void,
  formState: any,
) => ({
  selectedRowKeys: selectedCaseIds,
  onChange: (keys: React.Key[], selectedRows: CaseItem[]) => {
    const newSelectedCaseIds = keys.map(key => key.toString())
    setSelectedCaseIds(newSelectedCaseIds)
    updateTemplateSelectedCountMap(newSelectedCaseIds)
  },
  onSelectAll: (selected: boolean) => {
    if (selected) {
      const currentPageIds = (caseData.list || [])
        .map(item => item.resourceId?.toString())
        .filter(id => id !== undefined)
      const newSelectedCaseIds = [...new Set([...selectedCaseIds, ...currentPageIds])]
      setSelectedCaseIds(newSelectedCaseIds)
      updateTemplateSelectedCountMap(newSelectedCaseIds)
    } else {
      const currentPageIds = (caseData.list || [])
        .map(item => item.resourceId?.toString())
        .filter(id => id !== undefined)
      const newSelectedCaseIds = selectedCaseIds.filter(id => !currentPageIds.includes(id))
      setSelectedCaseIds(newSelectedCaseIds)
      updateTemplateSelectedCountMap(newSelectedCaseIds)
    }
  },
  onSelect: (record: CaseItem, selected: boolean) => {
    const recordId = record.resourceId.toString()
    if (selected) {
      const newSelectedCaseIds = [...selectedCaseIds, recordId]
      setSelectedCaseIds(newSelectedCaseIds)
      updateTemplateSelectedCountMap(newSelectedCaseIds)
    } else {
      const newSelectedCaseIds = selectedCaseIds.filter(id => id !== recordId)
      setSelectedCaseIds(newSelectedCaseIds)
      updateTemplateSelectedCountMap(newSelectedCaseIds)
    }
  },
  getCheckboxProps: (record: CaseItem) => ({
    disabled:
      record.type === 'system' ||
      (formState.executeType === 4 &&
        selectedCaseIds.length >= 10 &&
        !selectedCaseIds.includes(record.resourceId.toString())),
  }),
})
